import { AxiosError } from "axios";
import moment from "moment";
import { type AnyAction } from "redux";
import { type ShopfrontDispatch } from "../../_Bootstrap/hooks";
import { store } from "../../_Bootstrap/RoutingAndStore";
import { storeHostConnections } from "../../Utilities/Domain/HostConnections/HostConnections";
import { type HostConnectionTypeEnum } from "../../Utilities/Domain/HostConnections/HostTypes";
import { type ShopfrontThunk } from "../../Utilities/Generic/ActionsHelper";
import { lowerCase } from "../../Utilities/Generic/Strings";
import { getOption, type OptionValue, setOption } from "../../Utilities/Platform/Options/Options";
import { storeEnterprisePolicies } from "../../Utilities/Platform/Policies/Policies";
import Request, { expectPaymentRequired, PaymentRequiredError } from "../../Utilities/Platform/Request";
import { changeRegister } from "../General/LocationActions";
import { offline } from "../Information/InformationActions";
import { prepareSale } from "../SellScreen/SellScreenActions";
import {
    USER_LOADED,
    USER_LOGIN,
    USER_LOGOUT,
    USER_QUICK_MENU,
} from "./UserVariables";

export type User = {
    id: string;
    name: string;
    username: string;
    role?: string;
    email?: string;
    phone?: string;
    image: string;
    offline?: boolean;
    isAdmin?: boolean;
    quickMenu: QuickMenuType;
    darkMode: "dark" | "light" | "auto" | "default" | null;
};

interface UserLoginAction {
    type: typeof USER_LOGIN;
    user: User;
}

interface UserLoadedAction {
    type: typeof USER_LOADED;
    user: User;
}

export interface UserLogoutAction {
    type: typeof USER_LOGOUT;
}

export type QuickMenuType = Array<{
    id: number;
    url: string;
    name: string;
    external: boolean;
}>;

interface UpdateQuickMenuAction {
    type: typeof USER_QUICK_MENU;
    quickMenu: QuickMenuType;
}

export type UserActionTypes = UserLoginAction | UserLoadedAction | UserLogoutAction | UpdateQuickMenuAction;

export type UserState = User | {
    id: false;
};

export function userLogin(user: User): UserActionTypes {
    // Remove the localStorage value
    setOption("loggedout", false);

    return {
        type: USER_LOGIN,
        user,
    };
}

export function userLoaded(user: User): ShopfrontThunk {
    return (dispatch): void => {
        dispatch({
            type: USER_LOADED,
            user,
        });

        dispatch(prepareSale());
    };
}

export function userLogout(force = false): ShopfrontThunk {
    return (dispatch, getState): void => {
        const state = getState();

        // Check if offline user with sale in progress and do nothing if not forcing
        const sale = state.sellscreen.sale;

        if(!force && (state.user.id === false || state.user.offline && (sale.products.length || sale.customer))) {
            return;
        }

        // Set the localStorage value
        setOption("loggedout", true);

        // Send the logout to the server
        // Request.post('/logout'); // Ignore the response

        dispatch({
            type: USER_LOGOUT,
        });

        // Unset location if admin to force socket presence to reset
        if(state.user.id !== false && state.user.isAdmin) {
            dispatch(changeRegister());
        }
    };
}

export function updateQuickMenu(quickMenu: QuickMenuType): AnyAction {
    return {
        type: USER_QUICK_MENU,
        quickMenu,
    };
}

export interface AuthenticatedViewer extends User {
    passwordReset: boolean;
}

export async function getAuthenticatedViewer(): Promise<AuthenticatedViewer> {
    if(process.env.NODE_ENV !== "test") {
        performance.mark("SF-LOGIN-CU-REQUEST-START");
    }

    // Get logged in user
    if(window && window.location && window.location.pathname === "/settings/account") {
        expectPaymentRequired();
    }

    try {
        const response = await Request.get<{
            id: string;
            name: string;
            username: string;
            image: string | null;
            isAdmin: boolean;
            quickMenu: Array<{
                id: number;
                url: string;
                name: string;
                external: boolean;
            }>;
            darkMode: "DEFAULT" | "DARK" | "LIGHT" | "AUTO" | null;
            pendingPasswordReset: boolean;
            hostConnections: Array<{
                id: string;
                type: HostConnectionTypeEnum;
                outlet: string;
                priceSet: string | null;
                settings: Record<string, unknown>;
            }>;
            policies: {
                public: Record<string, OptionValue>;
                private: Record<string, OptionValue>;
            };
            integrations: {
                accountsFlow: {
                    enabled: boolean;
                };
            };
        }>("/user/info");

        if(response.status !== 200) {
            throw new Error("Invalid response provided when loading viewer");
        }

        if(process.env.NODE_ENV !== "test") {
            performance.mark("SF-LOGIN-CU-REQUEST-END");
            performance.measure(
                "Shopfront Current User Request",
                "SF-LOGIN-CU-REQUEST-START",
                "SF-LOGIN-CU-REQUEST-END"
            );
        }

        const { policies, hostConnections, integrations, pendingPasswordReset, ...viewer } = response.data;

        // Update the users that have been logged into this device
        const loggedInUsers = getOption("device_users");

        setOption("device_users", {
            ...loggedInUsers,
            [viewer.id]: moment().toISOString(true),
        });

        // Update enterprise policies
        // TODO: storeEnterprisePolicies(policies.private);
        storeEnterprisePolicies(policies.public);

        // Update host connections
        storeHostConnections(hostConnections);

        // Set whether accounts flow is enabled or not
        setOption("accounts_flow_enabled", integrations.accountsFlow.enabled);

        return {
            ...viewer,
            passwordReset: pendingPasswordReset,
            image        : viewer.image ?? "",
            darkMode     : viewer.darkMode === null ? null : lowerCase(viewer.darkMode),
        };
    } catch(err) {
        if(err instanceof PaymentRequiredError) {
            return {
                id           : "expired",
                name         : "Payment Required",
                username     : "",
                image        : "",
                offline      : false,
                isAdmin      : false,
                quickMenu    : [],
                darkMode     : "default",
                passwordReset: false,
            };
        }

        if(err instanceof AxiosError && err.response && err.response.status >= 500 && err.response.status < 600) {
            (store.dispatch as ShopfrontDispatch)(offline());
        }

        throw err;
    }
}
